<template>
  <div class="item-management">
    <div class="page-header">
      <div>
        <h2>按次消费商品</h2>
        <p>配置有库存实体物和无库存按次项目，库存与资料权限分开管理。</p>
      </div>
      <button class="primary-btn" @click="openCreateModal">新增项目</button>
      <input ref="tableImageInput" type="file" accept="image/*" class="hidden-input" @change="uploadTableImage" />
    </div>

    <div class="filter-card">
      <div class="filter-controls">
        <select v-model="selectedStoreId" class="form-select" @change="loadItems">
          <option value="">请选择门店</option>
          <option v-for="store in storeList" :key="store.id" :value="store.id">{{ store.storeName }}</option>
        </select>
        <select v-model="filterType" class="form-select type-select" @change="loadItems">
          <option value="">全部类型</option>
          <option value="STOCK">有库存实体物</option>
          <option value="SERVICE">无库存项目</option>
        </select>
      </div>
      <button class="secondary-btn refresh-btn" @click="loadItems">刷新</button>
    </div>

    <div class="table-card">
      <div class="table-scroll">
      <table class="item-table">
        <colgroup>
          <col class="col-image" />
          <col class="col-name" />
          <col class="col-type" />
          <col class="col-price" />
          <col class="col-barcode" />
          <col class="col-stock" />
          <col class="col-status" />
          <col class="col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th>图片</th>
            <th>名称</th>
            <th>类型</th>
            <th>金额</th>
            <th>条码</th>
            <th>库存</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="8" class="empty-cell">加载中...</td>
          </tr>
          <tr v-else-if="items.length === 0">
            <td colspan="8" class="empty-cell">暂无项目</td>
          </tr>
          <tr v-else v-for="item in items" :key="item.itemId">
            <td>
              <div class="table-image-wrap">
                <img v-if="item.imageUrl" :src="normalizeImageUrl(item.imageUrl)" class="item-image" alt="商品图" />
                <span v-else class="image-placeholder">无</span>
                <button class="image-change-btn" @click="openTableImageUpload(item)">换图</button>
              </div>
            </td>
            <td>{{ item.name }}</td>
            <td>{{ item.itemTypeText }}</td>
            <td>{{ formatMoney(item.price) }}元</td>
            <td>{{ item.barcode || '-' }}</td>
            <td>{{ item.itemType === 'STOCK' ? item.stockQuantity : '-' }}</td>
            <td>
              <span class="status-badge" :class="{ disabled: !item.enabled }">{{ item.enabled ? '启用' : '停用' }}</span>
            </td>
            <td>
              <div class="actions">
                <button class="link-btn" @click="openEditModal(item)">编辑</button>
                <button v-if="item.itemType === 'STOCK'" class="link-btn" @click="openStockModal(item, 'in')">入库</button>
                <button v-if="item.itemType === 'STOCK'" class="link-btn" @click="openStockModal(item, 'adjust')">调整</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>

    <div v-if="showItemModal" class="modal-overlay">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>{{ editingItem ? '编辑项目' : '新增项目' }}</h3>
          <button class="close-btn" @click="closeItemModal">×</button>
        </div>
        <form @submit.prevent="saveItem">
          <div class="form-grid">
            <label>
              <span>名称</span>
              <input v-model.trim="itemForm.name" class="form-input" required />
            </label>
            <label>
              <span>类型</span>
              <select v-model="itemForm.itemType" class="form-select">
                <option value="STOCK">有库存实体物</option>
                <option value="SERVICE">无库存项目</option>
              </select>
            </label>
            <label>
              <span>金额</span>
              <input v-model.number="itemForm.price" class="form-input" type="number" step="0.01" min="0.01" required />
            </label>
            <label v-if="itemForm.itemType === 'STOCK'">
              <span>条形码</span>
              <div class="barcode-row">
                <input v-model.trim="itemForm.barcode" class="form-input" required />
                <button type="button" class="secondary-btn" :disabled="lookupLoading || !itemForm.barcode" @click="lookupBarcode">
                  {{ lookupLoading ? '识别中...' : '识别条码' }}
                </button>
              </div>
            </label>
            <label v-if="!editingItem && itemForm.itemType === 'STOCK'">
              <span>初始库存</span>
              <input v-model.number="itemForm.stockQuantity" class="form-input" type="number" min="0" />
            </label>
            <label>
              <span>状态</span>
              <select v-model="itemForm.enabled" class="form-select">
                <option :value="true">启用</option>
                <option :value="false">停用</option>
              </select>
            </label>
          </div>
          <div v-if="itemForm.imageUrl || itemForm.barcode" class="barcode-preview">
            <img v-if="itemForm.imageUrl" :src="normalizeImageUrl(itemForm.imageUrl)" class="preview-image" alt="商品图" />
            <div v-else class="preview-image placeholder">货</div>
            <div class="preview-copy">
              <strong>{{ itemForm.name || '待识别商品' }}</strong>
              <span>条码：{{ itemForm.barcode || '-' }}</span>
              <span v-if="itemForm.remark">{{ itemForm.remark }}</span>
            </div>
          </div>
          <div class="form-group">
            <span>图片</span>
            <div class="upload-row">
              <input ref="imageInput" type="file" accept="image/*" class="hidden-input" @change="uploadImage" />
              <button type="button" class="secondary-btn" @click="$refs.imageInput.click()">上传图片</button>
              <span class="upload-text">{{ itemForm.imageUrl || '未上传' }}</span>
            </div>
          </div>
          <div class="form-group">
            <span>备注</span>
            <textarea v-model.trim="itemForm.remark" class="form-textarea"></textarea>
          </div>
          <div class="modal-actions">
            <button type="button" class="secondary-btn" @click="closeItemModal">取消</button>
            <button type="submit" class="primary-btn" :disabled="saving">{{ saving ? '保存中...' : '保存' }}</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showStockModal" class="modal-overlay">
      <div class="modal small" @click.stop>
        <div class="modal-header">
          <h3>{{ stockMode === 'in' ? '商品入库' : '库存调整' }}</h3>
          <button class="close-btn" @click="closeStockModal">×</button>
        </div>
        <form @submit.prevent="saveStock">
          <p class="stock-desc">{{ stockItem.name }}，当前库存 {{ stockItem.stockQuantity }}</p>
          <label class="form-group">
            <span>{{ stockMode === 'in' ? '入库数量' : '调整后库存' }}</span>
            <input v-model.number="stockForm.quantity" class="form-input" type="number" min="1" required />
          </label>
          <label class="form-group">
            <span>备注</span>
            <textarea v-model.trim="stockForm.remark" class="form-textarea"></textarea>
          </label>
          <div class="modal-actions">
            <button type="button" class="secondary-btn" @click="closeStockModal">取消</button>
            <button type="submit" class="primary-btn" :disabled="saving">{{ saving ? '提交中...' : '确认' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'ItemManagement',
  data() {
    return {
      storeList: [],
      selectedStoreId: '',
      filterType: '',
      items: [],
      loading: false,
      saving: false,
      lookupLoading: false,
      showItemModal: false,
      editingItem: null,
      itemForm: this.getEmptyItemForm(),
      showStockModal: false,
      imageTargetItem: null,
      stockItem: null,
      stockMode: 'in',
      stockForm: {
        quantity: 1,
        remark: ''
      }
    }
  },
  mounted() {
    this.loadStores()
  },
  methods: {
    getEmptyItemForm() {
      return {
        storeId: '',
        name: '',
        itemType: 'STOCK',
        price: 0,
        imageUrl: '',
        barcode: '',
        spec: '',
        brand: '',
        goodsType: '',
        firmName: '',
        productFingerprint: '',
        stockQuantity: 0,
        enabled: true,
        remark: ''
      }
    },
    async loadStores() {
      const response = await request.get('/api/stores/getStores', { params: { page: 0, size: 1000 } })
      if (response.data.success) {
        this.storeList = response.data.data.content || []
        if (!this.selectedStoreId && this.storeList.length) {
          this.selectedStoreId = this.storeList[0].id
          this.loadItems()
        }
      }
    },
    async loadItems() {
      if (!this.selectedStoreId) {
        this.items = []
        return
      }
      this.loading = true
      try {
        const response = await request.get('/api/admin/items', {
          params: {
            storeId: this.selectedStoreId,
            itemType: this.filterType || undefined
          }
        })
        if (response.data.success) {
          this.items = response.data.data || []
        } else {
          throw new Error(response.data.message || '加载失败')
        }
      } catch (error) {
        alert(error.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    openCreateModal() {
      if (!this.selectedStoreId) {
        alert('请先选择门店')
        return
      }
      this.editingItem = null
      this.itemForm = this.getEmptyItemForm()
      this.itemForm.storeId = this.selectedStoreId
      this.showItemModal = true
    },
    openEditModal(item) {
      this.editingItem = item
      this.itemForm = {
        storeId: item.storeId,
        name: item.name,
        itemType: item.itemType,
        price: Number(item.price || 0),
        imageUrl: item.imageUrl || '',
        barcode: item.barcode || '',
        spec: item.spec || '',
        brand: item.brand || '',
        goodsType: item.goodsType || '',
        firmName: item.firmName || '',
        productFingerprint: item.productFingerprint || '',
        stockQuantity: Number(item.stockQuantity || 0),
        enabled: item.enabled !== false,
        remark: item.remark || ''
      }
      this.showItemModal = true
    },
    closeItemModal() {
      if (this.saving) return
      this.showItemModal = false
    },
    async lookupBarcode() {
      if (!this.selectedStoreId) {
        alert('请先选择门店')
        return
      }
      if (!this.itemForm.barcode) {
        alert('请先填写条形码')
        return
      }
      this.lookupLoading = true
      try {
        const response = await request.get(`/api/admin/items/barcode-lookup/${encodeURIComponent(this.itemForm.barcode)}`, {
          params: { storeId: this.selectedStoreId }
        })
        if (!response.data.success) {
          throw new Error(response.data.message || '条码识别失败')
        }
        const data = response.data.data || {}
        if (data.existing && data.item) {
          if (this.editingItem && data.item.itemId === this.editingItem.itemId) {
            alert('该条码已属于当前商品')
            return
          }
          this.showItemModal = false
          this.openStockModal(data.item, 'in')
          alert('商品已存在，已切换为入库操作')
          return
        }
        this.itemForm = {
          ...this.itemForm,
          itemType: 'STOCK',
          name: data.name || this.itemForm.name,
          barcode: data.barcode || this.itemForm.barcode,
          imageUrl: data.imageUrl || this.itemForm.imageUrl,
          spec: data.spec || '',
          brand: data.brand || '',
          goodsType: data.goodsType || '',
          firmName: data.firmName || '',
          productFingerprint: data.productFingerprint || '',
          remark: data.remark || this.itemForm.remark
        }
      } catch (error) {
        alert(error.message || '条码识别失败')
      } finally {
        this.lookupLoading = false
      }
    },
    async saveItem() {
      if (this.itemForm.itemType === 'STOCK' && !this.itemForm.barcode) {
        alert('有库存商品必须填写条形码')
        return
      }
      this.saving = true
      try {
        const url = this.editingItem ? `/api/admin/items/${this.editingItem.itemId}` : '/api/admin/items'
        const method = this.editingItem ? 'put' : 'post'
        const response = await request[method](url, this.itemForm)
        if (response.data.success) {
          this.showItemModal = false
          await this.loadItems()
        } else {
          throw new Error(response.data.message || '保存失败')
        }
      } catch (error) {
        alert(error.message || '保存失败')
      } finally {
        this.saving = false
      }
    },
    async uploadImage(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const formData = new FormData()
      formData.append('file', file)
      try {
        const response = await request.post('/api/admin/items/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (response.data.success) {
          this.itemForm.imageUrl = response.data.data
        } else {
          throw new Error(response.data.message || '上传失败')
        }
      } catch (error) {
        alert(error.message || '上传失败')
      } finally {
        event.target.value = ''
      }
    },
    openTableImageUpload(item) {
      this.imageTargetItem = item
      this.$refs.tableImageInput.click()
    },
    async uploadTableImage(event) {
      const file = event.target.files && event.target.files[0]
      if (!file || !this.imageTargetItem) return
      const formData = new FormData()
      formData.append('file', file)
      this.saving = true
      try {
        const uploadResponse = await request.post('/api/admin/items/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (!uploadResponse.data.success) {
          throw new Error(uploadResponse.data.message || '上传失败')
        }
        const updateResponse = await request.put(`/api/admin/items/${this.imageTargetItem.itemId}/image`, {
          imageUrl: uploadResponse.data.data
        })
        if (!updateResponse.data.success) {
          throw new Error(updateResponse.data.message || '更新图片失败')
        }
        await this.loadItems()
      } catch (error) {
        alert(error.message || '更新图片失败')
      } finally {
        this.saving = false
        this.imageTargetItem = null
        event.target.value = ''
      }
    },
    openStockModal(item, mode) {
      this.stockItem = item
      this.stockMode = mode
      this.stockForm = { quantity: mode === 'adjust' ? Number(item.stockQuantity || 0) : 1, remark: '' }
      this.showStockModal = true
    },
    closeStockModal() {
      if (this.saving) return
      this.showStockModal = false
    },
    async saveStock() {
      this.saving = true
      try {
        const path = this.stockMode === 'in' ? 'stock/in' : 'stock/adjust'
        const response = await request.post(`/api/admin/items/${this.stockItem.itemId}/${path}`, this.stockForm)
        if (response.data.success) {
          this.showStockModal = false
          await this.loadItems()
        } else {
          throw new Error(response.data.message || '提交失败')
        }
      } catch (error) {
        alert(error.message || '提交失败')
      } finally {
        this.saving = false
      }
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2)
    },
    normalizeImageUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      const value = String(url).replace(/\\/g, '/')
      if (value.startsWith('/api/')) return value
      if (value.startsWith('/files/')) return `/api${value}`
      const uploadRoot = '/home/games/'
      const uploadRootIndex = value.indexOf(uploadRoot)
      if (uploadRootIndex >= 0) {
        return `/api/files/${value.substring(uploadRootIndex + uploadRoot.length)}`
      }
      if (value.includes('/items/')) {
        return `/api/files/items/${value.split('/items/')[1]}`
      }
      if (value.startsWith('items/')) {
        return `/api/files/${value}`
      }
      return `/api/files/${value.replace(/^\/?files\//, '').replace(/^\/?/, '')}`
    }
  }
}
</script>

<style scoped>
.item-management {
  padding: 28px;
  max-width: 1280px;
  margin: 0 auto;
  box-sizing: border-box;
}
.page-header,
.filter-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.page-header h2 {
  margin: 0 0 6px;
  font-size: 24px;
  line-height: 1.2;
  color: #0f172a;
}
.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}
.filter-card,
.table-card {
  background: #fff;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  border: 1px solid #eef2f7;
}
.filter-card {
  align-items: stretch;
}
.filter-controls {
  display: grid;
  grid-template-columns: minmax(260px, 1.1fr) minmax(220px, 1fr);
  gap: 14px;
  flex: 1;
  min-width: 0;
}
.form-select,
.form-input,
.form-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
  height: 40px;
  background: #fff;
  color: #0f172a;
}
.form-textarea {
  min-height: 80px;
  height: auto;
}
.table-scroll {
  width: 100%;
  overflow-x: auto;
}
.item-table {
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
  table-layout: fixed;
}
.col-image { width: 112px; }
.col-name { width: 210px; }
.col-type { width: 160px; }
.col-price { width: 110px; }
.col-barcode { width: 180px; }
.col-stock { width: 90px; }
.col-status { width: 90px; }
.col-actions { width: 150px; }
thead th {
  background: #f8fafc;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}
th,
td {
  padding: 14px 12px;
  border-bottom: 1px solid #eef2f7;
  text-align: left;
  vertical-align: middle;
  font-size: 14px;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
td:nth-child(7),
td:nth-child(8),
th:nth-child(7),
th:nth-child(8) {
  text-align: center;
}
tbody tr:hover {
  background: #fbfdff;
}
.empty-cell {
  text-align: center;
  color: #64748b;
  padding: 42px 0;
}
.item-image {
  width: 54px;
  height: 54px;
  object-fit: cover;
  border-radius: 12px;
  display: block;
}
.table-image-wrap {
  width: 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.table-image-wrap .image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 12px;
  background: #f1f5f9;
}
.image-change-btn {
  display: block;
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #2563eb;
  font-size: 12px;
  cursor: pointer;
}
.image-placeholder {
  color: #94a3b8;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  background: #ecfdf5;
  color: #047857;
  font-size: 13px;
  font-weight: 700;
}
.status-badge.disabled {
  background: #f1f5f9;
  color: #64748b;
}
.actions {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  min-height: 26px;
}
.primary-btn,
.secondary-btn,
.link-btn {
  border: 0;
  border-radius: 8px;
  padding: 9px 14px;
  cursor: pointer;
  white-space: nowrap;
  line-height: 1;
}
.primary-btn {
  background: #2563eb;
  color: #fff;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.18);
}
.secondary-btn {
  background: #f1f5f9;
  color: #334155;
}
.refresh-btn {
  width: 74px;
  height: 40px;
  padding: 0;
}
.link-btn {
  background: transparent;
  color: #2563eb;
  padding: 0;
  font-size: 13px;
}
.link-btn:hover,
.image-change-btn:hover {
  color: #1d4ed8;
  text-decoration: underline;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal {
  width: 720px;
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  padding: 22px;
}
.modal.small {
  width: 420px;
}
.modal-header,
.modal-actions,
.upload-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.close-btn {
  border: 0;
  background: transparent;
  font-size: 28px;
  cursor: pointer;
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}
.form-group {
  display: block;
  margin-top: 14px;
}
.form-group span,
.form-grid span {
  display: block;
  margin-bottom: 8px;
  color: #475569;
}
.barcode-row {
  display: flex;
  gap: 10px;
}
.barcode-row .form-input {
  flex: 1;
}
.barcode-row .secondary-btn {
  white-space: nowrap;
}
.barcode-preview {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: #eff6ff;
}
.preview-image {
  width: 72px;
  height: 72px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
  background: #dbeafe;
}
.preview-image.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  font-weight: 800;
}
.preview-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #64748b;
}
.preview-copy strong {
  color: #0f172a;
}
.preview-copy span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hidden-input {
  display: none;
}
.upload-text,
.stock-desc {
  color: #64748b;
}
@media (max-width: 900px) {
  .item-management {
    padding: 18px;
  }
  .page-header,
  .filter-card {
    align-items: stretch;
    flex-direction: column;
  }
  .filter-controls {
    grid-template-columns: 1fr;
  }
  .refresh-btn {
    width: 100%;
  }
}
</style>
